// AdminWebhooksPage — F9.7 FASE 3 · Admin Stripe webhook events viewer.
//
// Full-page table with filters, summary metrics, and modal payload inspector.
// Reads from GET /api/admin/webhooks (admin-only).
import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Clock, Eye, RefreshCw, Loader2 } from "lucide-react";

import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";

const WINDOWS = ["1h", "24h", "7d", "30d", "all"];
const EVENT_TYPES = [
    "",
    "checkout.session.completed",
    "invoice.paid",
    "invoice.payment_failed",
    "customer.subscription.created",
    "customer.subscription.updated",
    "customer.subscription.deleted",
    "payment_intent.succeeded",
    "payment_intent.payment_failed",
];
const STATUSES = ["", "processed", "failed", "pending"];

function StatusBadge({ event }) {
    if (event.processed === true) {
        return (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border"
                style={{ color: "#CCCCCC", backgroundColor: "rgba(204, 204, 204,0.1)", borderColor: "rgba(204, 204, 204,0.3)" }}>
                <CheckCircle2 className="w-3 h-3" /> processed
            </span>
        );
    }
    if (event.error) {
        return (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border"
                style={{ color: "#FAFAFA", backgroundColor: "rgba(250, 250, 250,0.1)", borderColor: "rgba(250, 250, 250,0.3)" }}>
                <XCircle className="w-3 h-3" /> failed
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border"
            style={{ color: "#A3A3A3", backgroundColor: "rgba(163, 163, 163,0.1)", borderColor: "rgba(163, 163, 163,0.3)" }}>
            <Clock className="w-3 h-3" /> pending
        </span>
    );
}

function relTime(iso) {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

export default function AdminWebhooksPage() {
    const [window, setWindow] = useState("7d");
    const [eventType, setEventType] = useState("");
    const [status, setStatus] = useState("");
    const [data, setData] = useState({ items: [], total: 0, summary: { total: 0, processed: 0, failed: 0 } });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ window, limit: "100" });
            if (eventType) params.set("event_type", eventType);
            if (status) params.set("status", status);
            const res = await apiFetch(`/admin/webhooks?${params.toString()}`);
            setData(res);
        } catch (_e) {
            // Silent fail — likely 401 on first mount before cookie propagates.
            // Next auto-trigger will succeed. Keep previous data in state.
        } finally {
            setLoading(false);
        }
    }, [window, eventType, status]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="min-h-screen" style={{ backgroundColor: "#0A0A0A" }}>
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-webhooks-page">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-[#CCCCCC]">Admin</p>
                        <h1 className="text-2xl md:text-3xl font-semibold text-[#FFFFFF] mt-1">Stripe webhooks</h1>
                        <p className="text-sm text-[#CCCCCC] mt-1">Ricevuti nel periodo selezionato</p>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        data-testid="admin-webhooks-refresh"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border text-[#FFFFFF] hover:bg-white/[0.04]"
                        style={{ borderColor: "#1A1A1A", backgroundColor: "#111111" }}
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Aggiorna
                    </button>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    {[
                        { label: "Totali", value: data.summary?.total ?? 0, color: "#FFFFFF" },
                        { label: "Processati", value: data.summary?.processed ?? 0, color: "#CCCCCC" },
                        { label: "Falliti", value: data.summary?.failed ?? 0, color: "#FAFAFA" },
                    ].map((c) => (
                        <div
                            key={c.label}
                            data-testid={`admin-webhooks-metric-${c.label.toLowerCase()}`}
                            className="rounded-xl border p-4"
                            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                        >
                            <p className="text-[11px] uppercase tracking-widest text-[#CCCCCC]">{c.label}</p>
                            <p className="mt-1 text-2xl font-semibold tabular-nums" style={{ color: c.color }}>
                                {c.value}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2 mb-4">
                    <select
                        value={window}
                        onChange={(e) => setWindow(e.target.value)}
                        data-testid="admin-webhooks-window"
                        className="rounded-md border px-3 py-1.5 text-sm text-[#FFFFFF]"
                        style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                    >
                        {WINDOWS.map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                    <select
                        value={eventType}
                        onChange={(e) => setEventType(e.target.value)}
                        data-testid="admin-webhooks-event-type"
                        className="rounded-md border px-3 py-1.5 text-sm text-[#FFFFFF]"
                        style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                    >
                        {EVENT_TYPES.map((t) => <option key={t} value={t}>{t || "Tutti gli eventi"}</option>)}
                    </select>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        data-testid="admin-webhooks-status"
                        className="rounded-md border px-3 py-1.5 text-sm text-[#FFFFFF]"
                        style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                    >
                        {STATUSES.map((s) => <option key={s} value={s}>{s || "Tutti gli stati"}</option>)}
                    </select>
                </div>

                {/* Table */}
                <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-widest text-[#CCCCCC] border-b" style={{ borderColor: "#1A1A1A" }}>
                                <th className="px-4 py-3">Ricevuto</th>
                                <th className="px-4 py-3">Tipo evento</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Stripe ID</th>
                                <th className="px-4 py-3 text-right">Azione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#CCCCCC]">
                                    <Loader2 className="w-5 h-5 animate-spin inline" />
                                </td></tr>
                            )}
                            {!loading && data.items.length === 0 && (
                                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#CCCCCC]">
                                    Nessun evento webhook nel periodo selezionato.
                                </td></tr>
                            )}
                            {!loading && data.items.map((ev) => (
                                <tr
                                    key={ev.stripe_event_id || ev.id}
                                    className="border-b hover:bg-white/[0.02]"
                                    style={{ borderColor: "#1A1A1A" }}
                                    data-testid={`admin-webhook-row-${ev.stripe_event_id || ev.id}`}
                                >
                                    <td className="px-4 py-3 text-[#CCCCCC] text-xs">
                                        {relTime(ev.received_at || ev.created_at)}
                                    </td>
                                    <td className="px-4 py-3 text-[#FFFFFF] font-mono text-xs">
                                        {ev.type || ev.event_type || "—"}
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge event={ev} /></td>
                                    <td className="px-4 py-3 text-[#999999] font-mono text-xs truncate max-w-[220px]">
                                        {(ev.stripe_event_id || ev.id || "—").slice(0, 24)}…
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => setSelected(ev)}
                                            data-testid={`admin-webhook-view-${ev.stripe_event_id || ev.id}`}
                                            className="inline-flex items-center gap-1 text-xs text-[#FFFFFF] hover:underline"
                                        >
                                            <Eye className="w-3.5 h-3.5" /> Dettaglio
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="text-xs text-[#999999] mt-3">
                    Totale filtrato: <span className="tabular-nums">{data.total}</span>
                </p>
            </main>

            {/* Modal */}
            {selected && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ backgroundColor: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
                    onClick={() => setSelected(null)}
                    data-testid="admin-webhook-modal"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-3xl rounded-xl border p-6 max-h-[90vh] overflow-auto"
                        style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="text-[11px] uppercase tracking-widest text-[#CCCCCC]">Webhook payload</p>
                                <h3 className="text-lg font-semibold text-[#FFFFFF] mt-1 font-mono">{selected.type}</h3>
                            </div>
                            <button
                                onClick={() => setSelected(null)}
                                className="text-[#CCCCCC] hover:text-white text-xl"
                            >×</button>
                        </div>
                        <pre
                            className="text-xs rounded-lg p-3 overflow-auto"
                            style={{ backgroundColor: "#0A0A0A", color: "#E5E5E5", maxHeight: "60vh" }}
                        >
                            {JSON.stringify(selected, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}
